// @mui material components
import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Button,
  Grid,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDInput from "theme/components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios.js";
import MDButton from "theme/components/MDButton";
import usePageTitle from "app/pageTitle";

function AiDataReportsPage() {
  const pageTitle = "AI Report Builder";
  usePageTitle(pageTitle);

  const { ref } = useParams();
  const navigate = useNavigate();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [refreshPage, setRefreshPage] = useState(false);
  const [reports, setReports] = useState([]);
  const [cRef, setCRef] = useState(null);
  const [aiPrompt, setAiPrompt] = useState(null);

  const textLimit = 200;

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`portal/campaigns/${ref}/ai/reports.json`)
        .then((result) => {
          console.log(result);
          setShowLoading(false);
          if (result.data.status === "success") {
            setCRef(result.data.data.reference);
            setReports(result.data.data.reports);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  async function createAIReport() {
    if (aiPrompt === null) {
      setErrMsg("User instruction is required");
      setSeverity("error");
      setOpen(true);
      return;
    }

    setShowLoading(true);
    const json = {
      reference: cRef,
      prompt: aiPrompt,
    };

    await axios
      .post("portal/campaigns/ai/reports/create.json", json)
      .then((result) => {
        setShowLoading(false);
        console.log(result);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          // navigate(`/data-forms/${result.data.data.reference}`);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function deleteAIReport(reportRef) {
    setShowLoading(true);
    await axios
      .get(`/portal/campaigns/ai/reports/${reportRef}/delete.json`)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <MDBox pt={3} pb={3}>
        <MDBox>
          <Button
            color="dark"
            startIcon={<Icon>west</Icon>}
            onClick={() => {
              navigate(`/data-captures/overview/${ref}`);
            }}
          >
            Back
          </Button>
        </MDBox>
        <MDBox mt={2}>
          <Card sx={{ height: "100%", marginTop: "0px" }}>
            <MDBox p={3}>
              <MDBox>
                <MDTypography variant="h6">AI Report Builder</MDTypography>
              </MDBox>
              <MDBox mt={3}>
                <MDBox>
                  <MDBox mb={2}>
                    <MDInput
                      value={aiPrompt}
                      multiline
                      minRows={3}
                      margin="dense"
                      label="Tell AI"
                      placeholder="Give me a summary by location"
                      type="text"
                      fullWidth
                      variant="outlined"
                      onChange={(e) => {
                        if (e.target.value.length > textLimit) {
                          return;
                        }
                        setAiPrompt(e.target.value);
                      }}
                    />
                    {aiPrompt && (
                      <MDTypography
                        align="right"
                        variant="subtitle2"
                        fontSize={10}
                        color={aiPrompt.length === textLimit ? "error" : "dark"}
                        fontWeight="bold"
                      >
                        {aiPrompt ? aiPrompt.length : 0} of {textLimit}
                      </MDTypography>
                    )}
                  </MDBox>
                  <MDBox align="right">
                    <MDButton
                      onClick={() => {
                        createAIReport();
                      }}
                      color="warning"
                      variant="outlined"
                      startIcon={<Icon>assistant</Icon>}
                    >
                      Ask Shalewa
                    </MDButton>
                  </MDBox>
                </MDBox>
              </MDBox>
            </MDBox>
          </Card>
        </MDBox>
        <MDBox mt={3}>
          {reports.map((row) => (
            <MDBox mb={5}>
              <Card sx={{ height: "100%", marginTop: "0px" }}>
                <MDBox p={3} display="flex" justifyContent="space-between">
                  <MDBox>
                    <MDTypography variant="h6" fontWeight="medium">
                      {row.title}
                    </MDTypography>
                    <MDTypography variant="h6" fontWeight="light">
                      {row.prompt}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDButton
                      onClick={() => {
                        navigate(`/data-ai_report/${row.reference}`);
                      }}
                      color="warning"
                      variant="outlined"
                      iconOnly
                    >
                      <Icon>summarize</Icon>
                    </MDButton>
                    &nbsp;&nbsp;
                    <MDButton
                      onClick={() => {
                        deleteAIReport(row.reference);
                      }}
                      color="error"
                      variant="outlined"
                      iconOnly
                    >
                      <Icon>delete</Icon>
                    </MDButton>
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
          ))}
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default AiDataReportsPage;
